import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deepClone } from '../../../../helpers';
import {
    CommodityUnit,
    IAdjustmentCommoditiesDetailsError,
    IAdjustmentCommoditiesResponse,
    IAdjustmentHistoryResponse,
    ICommoditiesResponse,
    ICommodityAdjustmentHistoryResponse,
    ICommodityDetails,
    ICommodityDetailsError,
    IContractFilter,
    IContractScaleTicket,
    IContractScaleTicketFilter,
    IContractsResponse,
    IFilter,
    IOpenScaleTicket,
    IOpenScaleTicketFilter,
    ITank,
} from '../interfaces/ManageCommoditiesInterface';
import { MANAGE_COMMODITIES_INITIAL_STATE } from '../constants/manageCommoditiesInitialState';
import {
    convertCommoditiesItem,
    convertCommodityItem,
} from './manageCommoditiesHelpers';
import { feedlotManagerFeatures } from '../../../../apps/feedlotManager/featureFlags/feedlotManagerFeatures';

const { isCA8488On_LbsToTonsToggle } = feedlotManagerFeatures;

export const ManageCommodities = createSlice({
    name: 'ManageCommodities',
    initialState: { ...MANAGE_COMMODITIES_INITIAL_STATE },
    reducers: {
        setManageCommoditiesAdjustmentsLoading: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.adjustmentsLoading = action.payload;
        },
        setManageCommoditiesLoading: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.commoditiesLoading = action.payload;
        },
        setManageCommoditiesAdjustmentsCanCallApi: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.adjustmentsCanCallApi = action.payload;
        },
        setManageCommoditiesCanCallApi: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.commoditiesCanCallApi = action.payload;
        },
        toggleCommoditiesUnit: state => {
            const newUnit =
                state.commoditiesUnit === CommodityUnit.Lbs
                    ? CommodityUnit.Tons
                    : CommodityUnit.Lbs;
            state.commodityDetails = convertCommodityItem(
                state.commodityDetails,
                ['price', 'billingPrice'],
                newUnit,
            );
            state.fetchedCommodityDetails = convertCommodityItem(
                state.fetchedCommodityDetails,
                ['price', 'billingPrice'],
                newUnit,
            );
            state.commodityAdjustmentHistoryList = convertCommoditiesItem(
                state.commodityAdjustmentHistoryList,
                ['price', 'billingPrice'],
                newUnit,
            );
            state.adjustmentCommoditiesDetails = convertCommoditiesItem(
                state.adjustmentCommoditiesDetails,
                [
                    'actualWeight',
                    'variance',
                    'historicWeight',
                    'inventoryAmount',
                    'billingPrice',
                ],
                newUnit,
            );
            state.adjustmentHistoryList = convertCommoditiesItem(
                state.adjustmentHistoryList,
                ['totalWeight', 'actualWeight', 'variance', 'billingPrice'],
                newUnit,
            );
            state.commoditiesList = convertCommoditiesItem(
                state.commoditiesList,
                ['inventoryAmount', 'price', 'billingPrice'],
                newUnit,
            );
            state.commoditiesUnit = newUnit;
        },
        setManageCommoditiesAdjustmentsFilter: (
            state,
            action: PayloadAction<IFilter>,
        ) => {
            state.adjustmentsFilter = action.payload;
        },
        setManageCommoditiesFilter: (state, action: PayloadAction<IFilter>) => {
            state.commoditiesFilter = action.payload;
        },
        setContractsFilter: (state, action: PayloadAction<IContractFilter>) => {
            state.contractsFilter = action.payload;
        },
        setCommodityAction: (state, action: PayloadAction<string>) => {
            state.commodityAction = action.payload;
        },
        setCommodityId: (state, action: PayloadAction<number>) => {
            state.commodityId = action.payload;
        },
        setCommoditiesList: (
            state,
            action: PayloadAction<ICommoditiesResponse[]>,
        ) => {
            state.commoditiesList = isCA8488On_LbsToTonsToggle
                ? convertCommoditiesItem(
                      action.payload,
                      ['inventoryAmount', 'price', 'billingPrice'],
                      state.commoditiesUnit,
                  )
                : action.payload;
        },
        setAdjustmentHistoryList: (
            state,
            action: PayloadAction<IAdjustmentHistoryResponse[]>,
        ) => {
            state.adjustmentHistoryList = isCA8488On_LbsToTonsToggle
                ? convertCommoditiesItem(
                      action.payload,
                      ['totalWeight', 'actualWeight', 'variance'],
                      state.commoditiesUnit,
                  )
                : action.payload;
        },
        setAdjustmentCommoditiesDetails: (
            state,
            action: PayloadAction<IAdjustmentCommoditiesResponse[]>,
        ) => {
            state.adjustmentCommoditiesDetails = isCA8488On_LbsToTonsToggle
                ? convertCommoditiesItem(
                      action.payload,
                      [
                          'actualWeight',
                          'variance',
                          'historicWeight',
                          'inventoryAmount',
                      ],
                      state.commoditiesUnit,
                  )
                : action.payload;
        },
        setCommodityCost: (
            state,
            action: PayloadAction<IAdjustmentCommoditiesResponse>,
        ) => {
            const changedCommodity = state.adjustmentCommoditiesDetails.find(
                commodity =>
                    commodity.commodityId === action.payload.commodityId,
            );
            if (changedCommodity) {
                changedCommodity.cost = action.payload.cost;
            }
        },
        setAdjustmentCommoditiesDetailsError: (
            state,
            action: PayloadAction<IAdjustmentCommoditiesDetailsError>,
        ) => {
            state.adjustmentCommoditiesDetailsError = action.payload;
        },
        setAdjustmentCommoditiesDetailsAccordingToDate: (
            state,
            action: PayloadAction<string>,
        ) => {
            const adjustmentCommoditiesDetailsCopy = deepClone(
                state.adjustmentCommoditiesDetails,
            );

            for (const index in adjustmentCommoditiesDetailsCopy) {
                adjustmentCommoditiesDetailsCopy[index]['date'] =
                    action.payload;
            }

            state.adjustmentCommoditiesDetails =
                adjustmentCommoditiesDetailsCopy;
        },
        setFetchedAdjustmentCommoditiesDetails: (
            state,
            action: PayloadAction<IAdjustmentCommoditiesResponse[]>,
        ) => {
            state.fetchedAdjustmentCommoditiesDetails = action.payload;
        },
        setCommodityDetails: (
            state,
            action: PayloadAction<ICommodityDetails>,
        ) => {
            state.commodityDetails = isCA8488On_LbsToTonsToggle
                ? convertCommodityItem(
                      action.payload,
                      ['price', 'billingPrice'],
                      state.commoditiesUnit,
                  )
                : action.payload;
        },
        setFetchedCommodityDetails: (
            state,
            action: PayloadAction<ICommodityDetails>,
        ) => {
            state.fetchedCommodityDetails = isCA8488On_LbsToTonsToggle
                ? convertCommodityItem(
                      action.payload,
                      ['price', 'billingPrice'],
                      state.commoditiesUnit,
                  )
                : action.payload;
        },
        setCommodityDetailsError: (
            state,
            action: PayloadAction<ICommodityDetailsError>,
        ) => {
            state.commodityDetailsError = action.payload;
        },
        setCommodityAdjustmentHistory: (
            state,
            action: PayloadAction<ICommodityAdjustmentHistoryResponse[]>,
        ) => {
            state.commodityAdjustmentHistoryList = isCA8488On_LbsToTonsToggle
                ? convertCommoditiesItem(
                      action.payload,
                      ['price', 'billingPrice'],
                      state.commoditiesUnit,
                  )
                : action.payload;
        },
        setEarliestGeneratedInvoiceCutoffDate: (
            state,
            action: PayloadAction<string>,
        ) => {
            state.earliestGeneratedInvoiceCutoffDate = action.payload;
        },

        setContracts: (state, action: PayloadAction<IContractsResponse[]>) => {
            state.contracts = action.payload;
        },

        appendContracts: (
            state,
            action: PayloadAction<IContractsResponse[]>,
        ) => {
            state.contracts = [...state.contracts, ...action.payload];
        },

        setSelectedContract: (
            state,
            action: PayloadAction<{ id: string; name: string }>,
        ) => {
            state.selectedContract = action.payload;
        },

        setSelectedContractError: (state, action: PayloadAction<boolean>) => {
            state.selectedContractError = action.payload;
        },

        setContractsLoading: (state, action: PayloadAction<boolean>) => {
            state.contractsLoading = action.payload;
        },

        setOpenScaleTicketLoading: (state, action: PayloadAction<boolean>) => {
            state.openScaleTicketLoading = action.payload;
        },

        setContractScaleTickets: (
            state,
            action: PayloadAction<IContractScaleTicket[]>,
        ) => {
            state.contractScaleTickets = action.payload;
        },

        appendContractScaleTickets: (
            state,
            action: PayloadAction<IContractScaleTicket[]>,
        ) => {
            state.contractScaleTickets = [
                ...state.contractScaleTickets,
                ...action.payload,
            ];
        },

        setContractHasScaleTickets: (
            state,
            action: PayloadAction<IContractScaleTicket[]>,
        ) => {
            const tickets = action.payload;
            state.contractHasScaleTickets = tickets.length > 0;
        },

        setContractScaleTicketsLoading: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.contractScaleTicketsLoading = action.payload;
        },

        setContractScaleTicketFilter: (
            state,
            action: PayloadAction<IContractScaleTicketFilter>,
        ) => {
            state.contractScaleTicketFilter = action.payload;
        },

        setOpenScaleTicketFilter: (
            state,
            action: PayloadAction<IOpenScaleTicketFilter>,
        ) => {
            state.openScaleTicketFilter = action.payload;
        },

        setOpenScaleTickets: (
            state,
            action: PayloadAction<IOpenScaleTicket[]>,
        ) => {
            state.openScaleTickets = action.payload;
        },

        appendOpenScaleTickets: (
            state,
            action: PayloadAction<IOpenScaleTicket[]>,
        ) => {
            state.openScaleTickets = [
                ...state.openScaleTickets,
                ...action.payload,
            ];
        },

        toggleOpenScaleTicketChecked: (
            state,
            action: PayloadAction<number[]>,
        ) => {
            state.openScaleTickets = state.openScaleTickets.map(ticket =>
                action.payload.includes(ticket.scaleTicketId)
                    ? { ...ticket, checked: !ticket.checked }
                    : ticket,
            );
        },

        setAllOpenScaleTicketsChecked: state => {
            const newCheckedState = !state.openScaleTicketsSelectAll;
            state.openScaleTicketsSelectAll = newCheckedState;
            state.openScaleTickets = state.openScaleTickets.map(ticket => ({
                ...ticket,
                checked: newCheckedState,
            }));
        },

        resetOpenScaleTicketsSelectAll: state => {
            state.openScaleTicketsSelectAll =
                MANAGE_COMMODITIES_INITIAL_STATE.openScaleTicketsSelectAll;
        },

        setTanks: (state, action: PayloadAction<ITank[]>) => {
            state.tanks = action.payload;
        },

        setCommoditiesSubmitPending: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.submitPending = action.payload;
        },

        setEnableNemNegOnCommodity: (state, action: PayloadAction<boolean>) => {
            state.enableNemNegOnCommodity = action.payload;
        },
    },
});

export const {
    setManageCommoditiesAdjustmentsLoading,
    setManageCommoditiesLoading,
    setManageCommoditiesAdjustmentsCanCallApi,
    setManageCommoditiesCanCallApi,
    toggleCommoditiesUnit,
    setManageCommoditiesAdjustmentsFilter,
    setManageCommoditiesFilter,
    setContractsFilter,
    setCommodityAction,
    setCommodityId,
    setCommoditiesList,
    setAdjustmentHistoryList,
    setAdjustmentCommoditiesDetails,
    setCommodityCost,
    setAdjustmentCommoditiesDetailsError,
    setFetchedAdjustmentCommoditiesDetails,
    setCommodityDetails,
    setFetchedCommodityDetails,
    setCommodityDetailsError,
    setCommodityAdjustmentHistory,
    setAdjustmentCommoditiesDetailsAccordingToDate,
    setEarliestGeneratedInvoiceCutoffDate,
    setContracts,
    appendContracts,
    setSelectedContract,
    setSelectedContractError,
    setContractsLoading,
    setContractScaleTickets,
    appendContractScaleTickets,
    setContractScaleTicketsLoading,
    setContractHasScaleTickets,
    setContractScaleTicketFilter,
    setOpenScaleTicketLoading,
    setOpenScaleTicketFilter,
    setOpenScaleTickets,
    appendOpenScaleTickets,
    toggleOpenScaleTicketChecked,
    setAllOpenScaleTicketsChecked,
    resetOpenScaleTicketsSelectAll,
    setTanks,
    setCommoditiesSubmitPending,
    setEnableNemNegOnCommodity,
} = ManageCommodities.actions;

const manageCommoditiesReducer = ManageCommodities.reducer;
export default manageCommoditiesReducer;
